import React, { useState } from 'react';
import api from '../utils/api';
import { Link } from 'react-router-dom';
import Navbar from '../dashboard-landing/Navbar'; // Assuming Navbar is in the dashboard-landing folder
import Footer from '../dashboard-landing/Footer'; // Assuming Footer is in the dashboard-landing folder
import { LockClosedIcon } from '@heroicons/react/24/outline';
import LoadingSpinner from './ui/LoadingSpinner';

/**
 * SecureForgotPassword Component
 * Provides a secure, multi-factor form for users to verify their identity 
 * before receiving a password reset email.
 * Styled with Tailwind CSS for a modern and responsive UI.
 */
const SecureForgotPassword = () => {
    const [formData, setFormData] = useState({
        email: '',
        idSuffix: '',
        phoneSuffix: '',
    });

    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [success, setSuccess] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        // Restrict input for numeric fields
        if (name === 'idSuffix' || name === 'phoneSuffix') {
            const numericValue = value.replace(/[^0-9]/g, '');
            setFormData((prev) => ({ ...prev, [name]: numericValue }));
        } else {
            setFormData((prev) => ({ ...prev, [name]: value }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setMessage('');
        setSuccess(false);
        setLoading(true);

        try {
            const res = await api.post(`/api/forgot-password/verify-reset-request`, formData);
            if (res.data.success) {
                setSuccess(true);
                setMessage('Verification passed. If an account exists with these details, a password reset email has been sent.');
            } else {
                // For security, show a generic message even on failure
                setSuccess(true); // Show as success to prevent user enumeration
                setMessage('If an account exists with these details, a password reset email has been sent.');
            }
        } catch (err) {
            console.error('Reset error:', err);
            // On error, still show a generic success message to prevent revealing user information
            setSuccess(true);
            setMessage('If an account exists with these details, a password reset email has been sent.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="flex flex-col min-h-screen bg-gray-50">
            <Navbar />

            {/* Loading Overlay */}
            {loading && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-2xl p-8 shadow-2xl">
                        <LoadingSpinner message="Verifying..." />
                    </div>
                </div>
            )}

            <main className="flex-grow flex items-center justify-center p-4">
                <div className="w-full max-w-md bg-white rounded-xl shadow-lg p-8 space-y-6">
                    <div className="text-center">
                        <LockClosedIcon className="mx-auto h-12 w-12 text-blue-600" />
                        <h2 className="mt-4 text-2xl font-bold text-gray-900">
                            Forgot Password
                        </h2>
                        <p className="mt-2 text-sm text-gray-600">
                            For your security, please verify your identity to request a password reset.
                        </p>
                    </div>

                    {/* Message Display */}
                    {message && (
                        <div className={`p-4 rounded-md ${success ? 'bg-green-100 border-l-4 border-green-500 text-green-700' : 'bg-red-100 border-l-4 border-red-500 text-red-700'}`} role="alert">
                            <p>{message}</p>
                        </div>
                    )}

                    {/* Hide form after successful submission */}
                    {!success && (
                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div>
                                <label htmlFor="email" className="sr-only">Email</label>
                                <input
                                    id="email"
                                    type="email"
                                    name="email"
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                                    value={formData.email}
                                    onChange={handleChange}
                                    placeholder="Email Address"
                                    required
                                    disabled={loading}
                                />
                            </div>
                            <div>
                                <label htmlFor="idSuffix" className="sr-only">ID Suffix</label>
                                <input
                                    id="idSuffix"
                                    type="text"
                                    name="idSuffix"
                                    placeholder="Last 4 digits of ID number"
                                    maxLength={4}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                                    value={formData.idSuffix}
                                    onChange={handleChange}
                                    required
                                    disabled={loading}
                                />
                            </div>
                            <div>
                                <label htmlFor="phoneSuffix" className="sr-only">Phone Suffix</label>
                                <input
                                    id="phoneSuffix"
                                    type="text"
                                    name="phoneSuffix"
                                    placeholder="Last 4 digits of phone number"
                                    maxLength={4}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                                    value={formData.phoneSuffix}
                                    onChange={handleChange}
                                    required
                                    disabled={loading}
                                />
                            </div>
                            <div>
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition disabled:opacity-50"
                                >
                                    {loading ? 'Verifying...' : 'Verify & Send Reset Email'}
                                </button>
                            </div>
                        </form>
                    )}

                    <div className="text-center">
                        <Link
                            to="/login"
                            className="text-sm font-medium text-blue-600 hover:underline"
                        >
                            Back to Login
                        </Link>
                    </div>
                </div>
            </main>

            <Footer />
        </div>
    );
};

export default SecureForgotPassword;
